import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatDate, formatTime, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, FilterChips, ListCard, Page, Row, Table, Td, Th } from '../../ui';
import { EnquiryPanel } from './EnquiryPanel';

/** § 11 — `new` → `in_progress` → `won` | `lost`, plus "Alle". */
const FILTERS = ['new', 'in_progress', 'won', 'lost', 'all'] as const;
export type EnquiryFilter = (typeof FILTERS)[number];

export const STATUS_TONE = {
  new: 'warn',
  in_progress: 'teal',
  won: 'success',
  lost: 'danger',
} as const;

/**
 * `/anfragen` (10-web-admin § 11).
 *
 * The list is the columns § 11 names and nothing more; everything personal —
 * phone, message — lives behind the panel, which fetches the one enquiry it
 * shows. New enquiries arrive over SSE (`enquiry`), which invalidates this key.
 */
export function Enquiries() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [filter, setFilter] = useState<EnquiryFilter>('new');

  const enquiries = useQuery({
    queryKey: qk.admin.area('enquiries', filter),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/enquiries', {
        params: { query: filter === 'all' ? {} : { status: filter } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (enquiries.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = enquiries.data?.items ?? [];
  const counts = enquiries.data?.counts;

  return (
    <Page>
      <ListCard title={t('admin.enquiries.title')}>
        <FilterChips
          value={filter}
          onChange={setFilter}
          options={FILTERS.map((value) => ({
            value,
            label: t(`admin.enquiries.filter.${value}`),
            count: counts?.[value],
          }))}
        />

        {items.length === 0 ? (
          <EmptyState>{t('admin.enquiries.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.enquiries.column.received')}</Th>
                <Th>{t('admin.enquiries.column.interest')}</Th>
                <Th>{t('admin.enquiries.column.who')}</Th>
                <Th align="right">{t('admin.enquiries.column.persons')}</Th>
                <Th>{t('admin.enquiries.column.fromWhen')}</Th>
                <Th>{t('admin.enquiries.column.status')}</Th>
                <Th>{t('admin.enquiries.column.assignee')}</Th>
              </>
            }
          >
            {items.map((enquiry) => (
              <Row key={enquiry.id} to={`/anfragen/${enquiry.id}`} selected={enquiry.id === id}>
                <Td bold>
                  {formatDate(enquiry.createdAt)}, {formatTime(enquiry.createdAt)}
                </Td>
                <Td>
                  <Chip tone="teal" label={t(`admin.enquiries.interest.${enquiry.interest}`)} />
                </Td>
                <Td>{enquiry.company ? `${enquiry.name} · ${enquiry.company}` : enquiry.name}</Td>
                <Td numeric>{enquiry.persons ?? '—'}</Td>
                <Td>{enquiry.fromWhen ?? '—'}</Td>
                <Td>
                  <Chip
                    tone={STATUS_TONE[enquiry.status]}
                    label={t(`admin.enquiries.filter.${enquiry.status}`)}
                  />
                </Td>
                <Td>{enquiry.assignedToName ?? '—'}</Td>
              </Row>
            ))}
          </Table>
        )}
      </ListCard>

      {id ? <EnquiryPanel id={id} onClose={() => navigate('/anfragen')} /> : null}
    </Page>
  );
}
